/**
 * Small binary writers for the effects comparer's capture kit and downloads (W3-04): CRC-32, PNG (8-bit RGB / RGBA,
 * Sub-filtered rows), a silent 16-bit PCM WAV and a flat ZIP (Deflate or Stored entries, fixed timestamps, so the
 * same input always gives the same bytes). Compression uses the platform's `CompressionStream` (browsers and Node).
 */

import type { RgbaImage } from './bench';

const CRC_TABLE = ((): Uint32Array => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

/** CRC-32 (IEEE, as used by PNG and ZIP) of `bytes`, continuing from `crc`. */
export function crc32(bytes: Uint8Array, crc = 0): number {
  let c = (crc ^ 0xffffffff) >>> 0;
  for (const b of bytes) c = ((CRC_TABLE[(c ^ b) & 0xff] ?? 0) ^ (c >>> 8)) >>> 0;
  return (c ^ 0xffffffff) >>> 0;
}

async function compress(bytes: Uint8Array, format: 'deflate' | 'deflate-raw'): Promise<Uint8Array> {
  const stream = new Blob([new Uint8Array(bytes)]).stream().pipeThrough(new CompressionStream(format));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function concat(parts: readonly Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

const ascii = (s: string): Uint8Array => Uint8Array.from(s, (ch) => ch.charCodeAt(0) & 0x7f);

function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  const v = new DataView(out.buffer);
  v.setUint32(0, data.length);
  out.set(ascii(type), 4);
  out.set(data, 8);
  v.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
}

/** PNG of an image: RGB (alpha dropped) unless `alpha`, every row Sub-filtered. */
export async function encodePng(img: RgbaImage, alpha = false): Promise<Uint8Array> {
  const ch = alpha ? 4 : 3;
  const { width: w, height: h, data } = img;
  const stride = w * ch + 1;
  const raw = new Uint8Array(stride * h);
  for (let y = 0; y < h; y++) {
    const row = y * stride;
    raw[row] = 1; // Sub
    for (let x = 0; x < w; x++) {
      for (let k = 0; k < ch; k++) {
        const v = data[(y * w + x) * 4 + k] ?? 0;
        const left = x > 0 ? (data[(y * w + x - 1) * 4 + k] ?? 0) : 0;
        raw[row + 1 + x * ch + k] = (v - left) & 0xff;
      }
    }
  }
  const ihdr = new Uint8Array(13);
  const v = new DataView(ihdr.buffer);
  v.setUint32(0, w);
  v.setUint32(4, h);
  ihdr[8] = 8;
  ihdr[9] = alpha ? 6 : 2;
  const signature = Uint8Array.of(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a);
  return concat([
    signature,
    pngChunk('IHDR', ihdr),
    pngChunk('IDAT', await compress(raw, 'deflate')),
    pngChunk('IEND', new Uint8Array(0)),
  ]);
}

/** A silent mono 16-bit PCM WAV of `seconds`. */
export function silentWav(seconds: number, rate = 22050): Uint8Array {
  const samples = Math.max(1, Math.round(seconds * rate));
  const dataBytes = samples * 2;
  const out = new Uint8Array(44 + dataBytes);
  const v = new DataView(out.buffer);
  out.set(ascii('RIFF'), 0);
  v.setUint32(4, 36 + dataBytes, true);
  out.set(ascii('WAVEfmt '), 8);
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  out.set(ascii('data'), 36);
  v.setUint32(40, dataBytes, true);
  return out;
}

export interface ZipEntry {
  /** ASCII path, '/'-separated, no leading '/'. */
  readonly path: string;
  readonly bytes: Uint8Array;
}

/** DOS date 1980-01-01, time 00:00 (fixed: the same files give the same archive). */
const DOS_DATE = 0x0021;

/** A flat ZIP: entries in the given order, Deflate when it is smaller, Stored otherwise. */
export async function zip(entries: readonly ZipEntry[]): Promise<Uint8Array> {
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const e of entries) {
    if (!/^[\x20-\x7e]+$/.test(e.path) || e.path.startsWith('/')) throw new Error(`zip: bad path ${e.path}`);
    const name = ascii(e.path);
    const crc = crc32(e.bytes);
    const deflated = await compress(e.bytes, 'deflate-raw');
    const stored = deflated.length >= e.bytes.length;
    const body = stored ? e.bytes : deflated;
    const method = stored ? 0 : 8;
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(8, method, true);
    lv.setUint16(12, DOS_DATE, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, body.length, true);
    lv.setUint32(22, e.bytes.length, true);
    lv.setUint16(26, name.length, true);
    local.set(name, 30);
    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(10, method, true);
    cv.setUint16(14, DOS_DATE, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, body.length, true);
    cv.setUint32(24, e.bytes.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);
    locals.push(local, body);
    centrals.push(central);
    offset += local.length + body.length;
  }
  const dir = concat(centrals);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, dir.length, true);
  ev.setUint32(16, offset, true);
  return concat([...locals, dir, end]);
}
